import { apiFetchResponse } from '../lib/apiFetch';

export type Market = 'MFR' | 'MEN' | 'TOR';
export const MARKETS: Market[] = ['MFR', 'MEN', 'TOR'];
export const MARKET_LABEL: Record<Market, string> = {
  MFR: 'Montreal FR',
  MEN: 'Montreal EN',
  TOR: 'Toronto',
};
export type Role = 'crematorium' | 'transport' | 'dropoff';
export const ROLES: Role[] = ['crematorium', 'transport', 'dropoff'];
export const ROLE_LABEL: Record<Role, string> = {
  crematorium: 'Crematorium',
  transport: 'Transport',
  dropoff: 'Drop-off and storage',
};

export interface RoleBucket {
  billed: number;
  unpaid: number;
  expected: number;
  missing: number;
}

export interface MarketMonth {
  n: number;
  paid: number;
  gross: number;
  refunds: number;
  tax: number;
  net: number;
  gov: number;
  urns: number;
  ship: number;
  courier: number;
  card_fee: number;
  delivery: number;
  crematorium: RoleBucket;
  transport: RoleBucket;
  dropoff: RoleBucket;
  partner: number;
  ads: number;
  owed: number;
  owed_n: number;
  contribution: number;
}

export interface FixedCost {
  id: string;
  name: string;
  amount: number;
  note?: string;
}

export interface RoleRate {
  rate: number | null;
  held_rate?: number | null;
}

export interface Settings {
  partner_rates: Record<string, Partial<Record<Role, RoleRate>>>;
  unit_costs: { urn: number; shipping: number; courier: number };
  card_fee: { pct: number; fixed: number };
  fixed_costs: FixedCost[];
  ads_manual: Record<string, Partial<Record<Market, number>>>;
}

export interface RateRow {
  partner_id: string;
  partner_name: string;
  role: Role;
  rate: number | null;
  held_rate: number | null;
  default_rate: number | null;
  default_held_rate: number | null;
  source: 'owner' | 'default';
  note: string;
  card: string | null;
  billed_avg_90d: number | null;
  billed_cases_90d: number;
}

export interface MissingBillRow {
  month: string;
  market: Market;
  role: Role;
  partner_id: string;
  partner_name: string;
  cases: number;
  expected: number;
  no_rate: boolean;
}

export interface UnpaidCase {
  funeral_id: number;
  name: string;
  month: string;
  market: Market;
  owed: number;
  costs: number;
}

export type AdsSource = 'api' | 'ads_daily' | 'manual' | 'none';

export interface Report {
  months: string[];
  generated_at: string;
  settings_ready: boolean;
  settings: Settings;
  markets: Record<string, Record<Market, MarketMonth>>;
  missing_bills: MissingBillRow[];
  unpaid_cases: UnpaidCase[];
  no_partner: { crematorium: number; transport: number };
  preneed: Record<string, Record<Market, { n: number; value: number; collected: number }>>;
  ads: {
    source_by_month: Record<string, AdsSource>;
    unmapped: { month: string; campaign: string; cost: number }[];
    through: string;
    error: string | null;
  };
  rates: RateRow[];
}

/** Thrown for the backend's owner check, so the page can say so instead of erroring. */
export class ForbiddenError extends Error {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetchResponse(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (res.status === 403) throw new ForbiddenError('Owner only');
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || `API error: ${res.status}`);
  }
  return res.json();
}

export const fetchReport = (last?: string, count = 3) =>
  call<Report>(`profitability?count=${count}${last ? `&last=${last}` : ''}`);

export const saveSetting = <K extends keyof Settings>(key: K, value: Settings[K]) =>
  call<{ settings: Settings }>(`profitability/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });

// --- sums over the backend's per-market monthly aggregates ------------------------------

export function sumMarketMonths(list: MarketMonth[]): MarketMonth {
  const bucket = (): RoleBucket => ({ billed: 0, unpaid: 0, expected: 0, missing: 0 });
  const z: MarketMonth = {
    n: 0,
    paid: 0,
    gross: 0,
    refunds: 0,
    tax: 0,
    net: 0,
    gov: 0,
    urns: 0,
    ship: 0,
    courier: 0,
    card_fee: 0,
    delivery: 0,
    crematorium: bucket(),
    transport: bucket(),
    dropoff: bucket(),
    partner: 0,
    ads: 0,
    owed: 0,
    owed_n: 0,
    contribution: 0,
  };
  for (const m of list)
    for (const k of Object.keys(z) as (keyof MarketMonth)[]) {
      const v = m[k];
      if (typeof v === 'number') (z as unknown as Record<string, number>)[k] += v;
      else for (const kk of Object.keys(v) as (keyof RoleBucket)[]) (z[k] as RoleBucket)[kk] += v[kk];
    }
  return z;
}

export const monthLabel = (m: string, style: 'short' | 'long' = 'long') => {
  const [y, mm] = m.split('-').map(Number);
  return new Date(Date.UTC(y, mm - 1, 15)).toLocaleDateString('en-CA', {
    month: 'short',
    year: style === 'long' ? 'numeric' : undefined,
    timeZone: 'UTC',
  });
};

export const fmt = (v: number) =>
  (v < -0.5 ? '−' : '') + '$' + Math.round(Math.abs(v)).toLocaleString('en-CA');
